import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAllStudents } from '../../../redux/studentRelated/studentHandle';
import { getAllSclasses } from '../../../redux/sclassRelated/sclassHandle';
import { addFee, getStudentFee, saveDraftFee, getAllFees, updateDraftFee, generateBillFromDraft } from '../../../redux/feeRelated/feeHandle';
import { underControl } from '../../../redux/feeRelated/feeSlice';
import {
    Box, Button, Container, MenuItem, Select, FormControl,
    InputLabel, TextField, Typography, Paper, Grid,
    Divider, IconButton, Card, InputAdornment, Stack,
    Avatar, CircularProgress, Dialog, DialogContent,
    Tooltip, Autocomplete, Chip
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    AttachMoney as AttachMoneyIcon,
    Person as PersonIcon,
    Event as EventIcon,
    Description as DescriptionIcon,
    AccountBalanceWallet as WalletIcon,
    Calculate as CalculateIcon,
    School as SchoolIcon,
    DirectionsBus as BusIcon,
    Hotel as HotelIcon,
    MenuBook as BookIcon,
    AddModerator as AddModeratorIcon,
    Print as PrintIcon,
    Settings as SettingsIcon,
    Visibility as VisibilityIcon,
    CheckCircle as CheckCircleIcon,
    Delete as DeleteIcon,
    PhotoCamera as PhotoCameraIcon,
    Badge as BadgeIcon,
    Class as ClassIcon,
    History as HistoryIcon
} from '@mui/icons-material';
import styled, { keyframes } from 'styled-components';
import { useToast } from '../../../context/ToastContext';
import useReceiptConfig from '../../../hooks/useReceiptConfig';
import { CustomNepaliDatePicker, getNepaliMonthOptions, getNepaliYearOptions, getCurrentBSDate, formatDatePart } from '../../../utils/customDate';
import FeeReceipt from './FeeReceipt';
import { extractLatestReceiptData } from '../../../utils/receiptDocumentTitle';
import axios from 'axios';

const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const normalizeMonthToken = (token) => (token || '').toLowerCase().replace(/[^a-z]/g, '');

const getStoredMonthParts = (monthText) => {
    if (!monthText || typeof monthText !== 'string') return [];

    const yearMatches = monthText.match(/\b\d{4}\b/g);
    const parsedYear = yearMatches?.[yearMatches.length - 1];
    if (!parsedYear) return [];

    const monthOptions = getNepaliMonthOptions();
    const valueByLabel = monthOptions.reduce((acc, option) => {
        acc[normalizeMonthToken(option.label)] = option.value;
        return acc;
    }, {
        baisakh: '01',
        asadh: '03',
        shravan: '04',
        aswin: '06',
        paush: '09'
    });

    return monthText
        .replace(parsedYear, '')
        .replace(/\u2013|\u2014/g, '-')
        .split('-')
        .map(part => valueByLabel[normalizeMonthToken(part)])
        .filter(Boolean)
        .map(value => ({ year: parsedYear, value }));
};

const parseStoredFeeDescription = (fullDescription, categories) => {
    const emptyCategoryAmounts = categories.reduce((acc, category) => {
        acc[category.id] = '';
        return acc;
    }, {});

    if (!fullDescription || typeof fullDescription !== 'string') {
        return {
            categoryAmounts: emptyCategoryAmounts,
            additionalNotes: ''
        };
    }

    const notes = [];
    const parsedAmounts = { ...emptyCategoryAmounts };
    const categoryIdByLabel = categories.reduce((acc, category) => {
        acc[category.label] = category.id;
        return acc;
    }, {});

    fullDescription
        .split(',')
        .map(part => part.trim())
        .filter(Boolean)
        .forEach((part) => {
            const amountMatch = part.match(/^(.*?):\s*Rs\.?\s*([0-9.,]+)(?:\s*-\s*(.*))?$/i);
            if (amountMatch) {
                const [, rawLabel, rawAmount, trailingNote] = amountMatch;
                const categoryId = categoryIdByLabel[rawLabel.trim()];
                if (categoryId) {
                    const parsedAmount = parseFloat(rawAmount.replace(/,/g, ''));
                    if (!Number.isNaN(parsedAmount)) {
                        parsedAmounts[categoryId] = parsedAmount.toString();
                    }

                    if (trailingNote?.trim()) {
                        notes.push(trailingNote.trim());
                    }
                    return;
                }
            }

            const sanitizedPart = part.replace(/^-\s*/, '').trim();
            if (/^(Discount|Advance Used):/i.test(sanitizedPart)) {
                return;
            }

            if (sanitizedPart) {
                notes.push(sanitizedPart);
            }
        });

    return {
        categoryAmounts: parsedAmounts,
        additionalNotes: notes.join(', ')
    };
};

const AddFee = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { currentUser } = useSelector((state) => state.user);
    const { studentsList } = useSelector((state) => state.student);
    const { sclassesList } = useSelector((state) => state.sclass);
    const { feesList, response, status, loading } = useSelector((state) => state.fee);

    const [selectedClass, setSelectedClass] = useState('');
    const [studentId, setStudentId] = useState('');

    // Used to prefill target student + class from ShowAllFees (and avoid showing fee history)
    const [prefillTarget, setPrefillTarget] = useState(null);

    const [feeHistory, setFeeHistory] = useState([]);
    const [feeHistoryClassName, setFeeHistoryClassName] = useState('');

    // Hide past fee history when pre-filling from ShowAllFees
    const showPastFeeHistory = !prefillTarget;
    const [year, setYear] = useState('2083');

    // If user arrived from a fees list row click, we prefill student/month/categories.
    // This flag prevents FeeSetup reset from wiping our prefilled amounts.
    const [isPrefillingFromFeesList, setIsPrefillingFromFeesList] = useState(false);
    // single select kept for backward compatibility (receipt/month label)
    const [month, setMonth] = useState('');
    // multi month selection (values are month labels e.g. "Baisakh")
    const [selectedMonths, setSelectedMonths] = useState([]);
    const [categoryAmounts, setCategoryAmounts] = useState({
        tuition: '',
        admission: '',
        annual: '',
        security: '',
        examination: '',
        computer: '',
        transportation: '',
        hostel: '',
        stationary: '',
        sports: '',
        transferCertificate: '',
        characterCertificate: '',
        specialTraining: '',
        meal: '',
        educationalTour: '',
        eca: '',
        educationalMaterial: '',
        extras: ''
    });
    const [totalMonthly, setTotalMonthly] = useState(0);
    const [duesAmount, setDuesAmount] = useState('');
    const [discount, setDiscount] = useState('');
    const [description, setDescription] = useState('');
    const [paymentStatus, setPaymentStatus] = useState('Unpaid');

    const [paidAmount, setPaidAmount] = useState('');
    const [payDate, setPayDate] = useState(getCurrentBSDate());
    const { showToast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    // Receipt Configuration State (persistent via localStorage)
    const [openPreview, setOpenPreview] = useState(false);
    const [isPrintingSaved, setIsPrintingSaved] = useState(false);
    const [savedReceiptData, setSavedReceiptData] = useState(null);
    const [receiptConfig, updateReceiptConfig] = useReceiptConfig({
        schoolName: currentUser?.schoolName || '',
        email: currentUser?.email || ''
    });

    const [newFieldName, setNewFieldName] = useState('');
    
    const prevMonthCountRef = React.useRef(1);
    const activeDraftId = location?.state?.mode === 'draft-edit' ? location?.state?.draftId : null;

    const parseSafe = (val) => {
        if (typeof val === 'number') return val;
        if (!val || val === '') return 0;
        return parseFloat(val.toString().replace(/,/g, '')) || 0;
    };

    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 200 * 1024) { // 200KB limit for base64 storage
                showToast("Logo file too large. Please use an image smaller than 200KB.", "warning");
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                updateReceiptConfig({ header: { logo: reader.result } });
            };
            reader.readAsDataURL(file);
        }
    };

    const months = getNepaliMonthOptions();

    const currentYear = new Date().getFullYear();

    const lockedMonthsByValue = React.useMemo(() => {
        const locked = new Map();
        if (!studentId || !year || !Array.isArray(feesList)) return locked;

        const selectedStudentFees = feesList.filter(
            (fee) => fee?.student?._id === studentId || fee?.student === studentId
        );

        selectedStudentFees.forEach((fee) => {
            (fee?.feeDetails || []).forEach((detail) => {
                const feeRecordId = (detail?.feeRecordId || fee?._id || '').toString();
                if (activeDraftId && feeRecordId === activeDraftId) return;

                getStoredMonthParts(detail?.month).forEach((part) => {
                    if (part.year !== year) return;
                    locked.set(part.value, {
                        billStatus: detail?.billStatus || fee?.billStatus || 'bill',
                        month: detail?.month
                    });
                });
            });
        });

        return locked;
    }, [feesList, studentId, year, activeDraftId]);

    const baseCategories = [
        { id: 'tuition', label: 'Tuition Fee', icon: <SchoolIcon />, color: '#667eea' },
        { id: 'admission', label: 'Admission Fee', icon: <AddModeratorIcon />, color: '#4caf50' },
        { id: 'annual', label: 'Annual Fee', icon: <EventIcon />, color: '#2196f3' },
        { id: 'security', label: 'Security Deposit', icon: <WalletIcon />, color: '#ff9800' },
        { id: 'examination', label: 'Examination Fee', icon: <DescriptionIcon />, color: '#9c27b0' },
        { id: 'computer', label: 'Computer Fee', icon: <CalculateIcon />, color: '#00bcd4' },
        { id: 'transportation', label: 'Transport Fee', icon: <BusIcon />, color: '#ffb74d' },
        { id: 'hostel', label: 'Hostel Fee', icon: <HotelIcon />, color: '#ba68c8' },
        { id: 'stationary', label: 'Stationary', icon: <BookIcon />, color: '#4dd0e1' },
        { id: 'sports', label: 'Sports Fee', icon: <CalculateIcon />, color: '#81c784' },
        { id: 'transferCertificate', label: 'Transfer Certificate', icon: <DescriptionIcon />, color: '#795548' },
        { id: 'characterCertificate', label: 'Character Certificate', icon: <DescriptionIcon />, color: '#607d8b' },
        { id: 'specialTraining', label: 'Special Training Fee', icon: <SchoolIcon />, color: '#e91e63' },
        { id: 'meal', label: 'Meal Fee', icon: <BusIcon />, color: '#ff5722' },
        { id: 'educationalTour', label: 'Educational Tour Fee', icon: <HotelIcon />, color: '#009688' },
        { id: 'eca', label: 'ECA Fee', icon: <CalculateIcon />, color: '#3f51b5' },
        { id: 'educationalMaterial', label: 'Educational Material Fee', icon: <BookIcon />, color: '#8bc34a' },
        { id: 'extras', label: 'Others', icon: <AddModeratorIcon />, color: '#f06292' }
    ];

    const customCategories = (receiptConfig.customFields || []).map(f => ({
        id: f.id,
        label: f.label,
        icon: <AddModeratorIcon />,
        color: '#ff9800',
        isCustom: true
    }));

    const categories = [...baseCategories, ...customCategories];

    const [transportStations, setTransportStations] = useState([]);
    const [selectedStation, setSelectedStation] = useState('');
    const [advanceBalanceOverride, setAdvanceBalanceOverride] = useState(null);

    const selectedStudent = React.useMemo(
        () => studentsList?.find((s) => s._id === studentId) || null,
        [studentsList, studentId]
    );
    const currentAdvanceBalance = advanceBalanceOverride !== null
        ? advanceBalanceOverride
        : (Number(selectedStudent?.advanceBalance) || 0);

    const getAdvanceApplied = (grossTotal) => {
        return Math.min(currentAdvanceBalance, Math.max(0, grossTotal));
    };

    const buildPreviewReceiptData = () => {
        if (isPrintingSaved) {
            if (savedReceiptData) return savedReceiptData;

            if (studentId && feesList?.length > 0) {
                const studentFees = feesList.filter(
                    (f) => f.student?._id === studentId || f.student === studentId
                );
                const latestFee = studentFees.length > 0 ? studentFees[studentFees.length - 1] : null;
                if (latestFee) {
                    return extractLatestReceiptData(latestFee, sclassesList);
                }
            }
        }

        const categoriesLabelMap = Object.fromEntries(categories.map((c) => [c.id, c.label]));
        const currentCategoriesDesc = Object.entries(categoryAmounts)
            .filter(([_, amt]) => parseSafe(amt) > 0)
            .map(([cat, amt]) => `${categoriesLabelMap[cat] || cat}: Rs. ${parseSafe(amt)}`)
            .join(', ');

        const grossTotalToPay = Math.max(0, totalMonthly + (parseFloat(duesAmount) || 0) - (parseFloat(discount) || 0));
        const advanceApplied = getAdvanceApplied(grossTotalToPay);
        const grandTotalToPay = Math.max(0, grossTotalToPay - advanceApplied);
        const monthLabels = getNepaliMonthOptions()
            .filter((m) => selectedMonths.includes(m.value))
            .map((m) => m.label);
        const combinedMonthLabel = monthLabels.length > 0
            ? `${monthLabels.join(' - ')} ${year}`
            : `${getNepaliMonthOptions().find((m) => m.value === month)?.label || month} ${year}`;

        return {
            studentName: studentsList?.find((s) => s._id === studentId)?.name || '---',
            rollNum: studentsList?.find((s) => s._id === studentId)?.rollNum || '---',
            className: sclassesList?.find((c) => c._id === selectedClass)?.sclassName || '---',
            month: combinedMonthLabel,
            amount: grandTotalToPay,
            grossAmount: grossTotalToPay,
            advanceUsed: advanceApplied,
            monthlyAmount: totalMonthly,
            duesAmount: parseSafe(duesAmount),
            discount: parseSafe(discount),
            status: grandTotalToPay === 0 && advanceApplied > 0 ? 'Paid' : paymentStatus,
            paidAmount: paymentStatus === 'Paid' ? grandTotalToPay : parseSafe(paidAmount),
            description: `${currentCategoriesDesc}${description ? ` - ${description}` : ''}${parseSafe(discount) > 0 ? `, - Discount: Rs. ${parseSafe(discount)}` : ''}${advanceApplied > 0 ? `, - Advance Used: Rs. ${advanceApplied}` : ''}`,
            paymentDate: payDate,
            billNumber: savedReceiptData?.billNumber
        };
    };

    const previewReceiptData = buildPreviewReceiptData();

    useEffect(() => {
        if (currentUser) {
            dispatch(getAllStudents(currentUser._id));
            dispatch(getAllSclasses(currentUser._id, "Sclass"));
            fetchTransportStations();
        }
        dispatch(underControl());
    }, [currentUser, dispatch]);

    const fetchTransportStations = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/TransportSetups/${currentUser._id}`);
            if (response.data && Array.isArray(response.data)) {
                setTransportStations(response.data);
            }
        } catch (error) {
            console.log("No transport stations found");
        }
    };

    const handleStationChange = (e) => {
        const stationId = e.target.value;
        setSelectedStation(stationId);
        
        if (stationId) {
            const station = transportStations.find(s => s._id === stationId);
            if (station) {
                const activeCount = selectedMonths.length || 1;
                handleCategoryChange('transportation', (station.price * activeCount).toString());
            }
        } else {
            handleCategoryChange('transportation', '');
        }
    };

    // Scale fee breakdown when selectedMonths length changes
    // IMPORTANT: Admission fee should be charged only once, so it must NOT scale with month count.
    useEffect(() => {
        const activeCount = selectedMonths.length || 1;
        const prevCount = prevMonthCountRef.current;
        if (activeCount !== prevCount) {
            setCategoryAmounts(prev => {
                const scaled = {};
                Object.keys(prev).forEach((key) => {
                    if (prev[key] === '') {
                        scaled[key] = '';
                        return;
                    }

                    const val = parseSafe(prev[key]);

                    if (key === 'admission' || key === 'annual' || key === 'examination') {
                        // lock one-time fees (admission/annual/examination) to their previous value (no scaling)
                        scaled[key] = val === 0 ? '' : val.toString();
                        return;
                    }

                    const scaledVal = (val * activeCount) / prevCount;
                    scaled[key] = scaledVal === 0 ? '' : scaledVal.toString();
                });
                return scaled;
            });
            prevMonthCountRef.current = activeCount;
        }
    }, [selectedMonths]);

    useEffect(() => {
        if (selectedClass && currentUser) {
            const fetchSetup = async () => {
                const resetFields = () => {
                    // When arriving from /Admin/fees row click, we already prefilled amounts.
                    // Avoid wiping them while FeeSetup is loading.
                    if (isPrefillingFromFeesList) return;
                    const emptyAmounts = {};
                    categories.forEach(cat => emptyAmounts[cat.id] = '');
                    setCategoryAmounts(emptyAmounts);
                    setTotalMonthly(0);
                    setSelectedStation('');
                    prevMonthCountRef.current = selectedMonths.length || 1;
                };


                try {
                    const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/FeeSetup/${currentUser._id}/${selectedClass}`);
                    if (response.data) {
                        const setup = response.data.feeDetails || {};
                        const customFieldsFromDb = response.data.customFields || [];
                        const newAmounts = {};
                        let total = 0;
                        const activeCount = selectedMonths.length || 1;
                        
                        categories.forEach(cat => {
                            newAmounts[cat.id] = '';
                        });

                        Object.keys(setup).forEach(key => {
                            if (setup[key] > 0) {
                                // Admission/Annual/Examination fees should be charged only once (not per selected month)
                                const scale = (key === 'admission' || key === 'annual' || key === 'examination') ? 1 : activeCount;
                                const scaledVal = setup[key] * scale;
                                newAmounts[key] = scaledVal.toString();
                                total += scaledVal;
                            }
                        });

                        customFieldsFromDb.forEach(field => {
                            if (field.amount > 0) {
                                const scale = (field.id === 'admission' || field.id === 'annual') ? 1 : activeCount;
                                const scaledVal = field.amount * scale;
                                newAmounts[field.id] = scaledVal.toString();
                                total += scaledVal;
                            }
                        });
                        
                        setCategoryAmounts(newAmounts);
                        setTotalMonthly(total);
                        setSelectedStation('');
                        prevMonthCountRef.current = activeCount;
                        showToast(`Standard fees for ${sclassesList.find(c => c._id === selectedClass)?.sclassName} loaded.`, "info");
                    } else {
                        resetFields();
                    }
                } catch (error) {
                    console.log("No fee setup for this class");
                    resetFields();
                }
            };
            fetchSetup();
        } else if (!selectedClass) {
            // Also reset if "All Classes" is selected
            const emptyAmounts = {};
            categories.forEach(cat => emptyAmounts[cat.id] = '');
            setCategoryAmounts(emptyAmounts);
            setTotalMonthly(0);
            prevMonthCountRef.current = selectedMonths.length || 1;
        }
    }, [selectedClass, currentUser, receiptConfig.customFields]);

    useEffect(() => {
        if (status === "added" && isSubmitting) {
            setIsSubmitting(false);
        } else if (response) {
            showToast(response, "error");
            setIsSubmitting(false);
        }
    }, [status, response, isSubmitting, showToast]);

    // Keep saved receipt data in sync when fees list refreshes after save
    useEffect(() => {
        if (!isPrintingSaved || !studentId || !feesList?.length) return;

        const studentFees = feesList.filter(
            (f) => f.student?._id === studentId || f.student === studentId
        );
        const latestFee = studentFees.length > 0 ? studentFees[studentFees.length - 1] : null;
        if (!latestFee) return;

        const receiptData = extractLatestReceiptData(latestFee, sclassesList);
        if (receiptData?.billNumber) {
            setSavedReceiptData(receiptData);
        }
    }, [feesList, isPrintingSaved, studentId, sclassesList]);

    // Load draft into the form when navigated from DraftBills
    useEffect(() => {
        const state = location?.state;
        const mode = state?.mode;
        const draftId = state?.draftId;

        if (!draftId || mode !== 'draft-edit') return;
        if (!currentUser?._id) return;
        if (!studentsList || !sclassesList) return;

        const load = async () => {
            try {
                // Load drafts for the selected draftId.
                // Use drafts-only endpoint so we never try to read a generated bill as a draft.
                const result = await axios.get(
                    `${process.env.REACT_APP_BASE_URL}/fee/drafts-only/${currentUser._id}`
                );


                const drafts = Array.isArray(result.data) ? result.data : [];
                const draft = drafts.find(d => d?._id === draftId);
                if (!draft) {
                    showToast('Draft not found', 'error');
                    return;
                }

                const loadedStudentId = draft?.student?._id || draft?.student;
                setStudentId(loadedStudentId || '');

                // Set the class from the draft's student data so the class dropdown is populated
                const loadedClassId = draft?.student?.sclassName?._id || draft?.student?.sclassName || '';
                if (loadedClassId) {
                    setSelectedClass(loadedClassId);
                }

                // Prevent the FeeSetup useEffect from wiping the draft's category amounts
                setIsPrefillingFromFeesList(true);

                const details = Array.isArray(draft.feeDetails) ? draft.feeDetails : [];
                const firstDetail = details[0] || null;
                if (!firstDetail) return;

                // Parse month/year from stored label like: "Baisakh 2083" or "Baisakh - Jestha 2083"
                // We store combined month label when creating; here we try to recover best-effort.
                const detailMonthStr = (firstDetail.month || '').trim();
                const lastSpace = detailMonthStr.lastIndexOf(' ');
                const yearPart = lastSpace >= 0 ? detailMonthStr.slice(lastSpace + 1) : year;
                setYear(yearPart || '2083');

                const monthPart = lastSpace >= 0 ? detailMonthStr.slice(0, lastSpace) : detailMonthStr;
                const monthLabels = monthPart.split('-').map(s => s.trim()).filter(Boolean);
                const months = getNepaliMonthOptions().filter(m => monthLabels.includes(m.label)).map(m => m.value);
                if (months.length) {
                    setSelectedMonths(months);
                    setMonth(months[months.length - 1]);
                }

                const { categoryAmounts: parsedCategoryAmounts, additionalNotes } = parseStoredFeeDescription(
                    firstDetail.description || '',
                    categories
                );

                setCategoryAmounts(prev => ({ ...prev, ...parsedCategoryAmounts }));
                setTotalMonthly(Number(firstDetail.monthlyAmount) || 0);
                setDuesAmount(firstDetail.duesAmount ? String(firstDetail.duesAmount) : '');
                setDiscount(firstDetail.discount ? String(firstDetail.discount) : '');

                setDescription(additionalNotes);
                setPaymentStatus(firstDetail.status || 'Unpaid');
                setPaidAmount(firstDetail.paidAmount !== undefined && firstDetail.paidAmount !== null ? String(firstDetail.paidAmount) : '');
                setPayDate(firstDetail.paymentDate || getCurrentBSDate());
                setSelectedStation(firstDetail.transportStation || '');

                prevMonthCountRef.current = months.length || 1;

                // Ensure transport price is recalculated based on loaded station + months
                if (firstDetail.transportStation) {
                    const activeCount = months.length || 1;
                    const station = transportStations.find(s => s._id === firstDetail.transportStation);
                    if (station) {
                        setCategoryAmounts(prev => ({
                            ...prev,
                            transportation: (station.price * activeCount).toString(),
                        }));
                    }
                }
            } catch (err) {
                showToast('Failed to load draft', 'error');
            }
        };

        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location?.state, currentUser?._id, studentsList, sclassesList]);

    // Fetch and calculate outstanding dues when student changes
    useEffect(() => {
        if (studentId) {
            dispatch(getStudentFee(studentId));
        }
        setAdvanceBalanceOverride(null);
    }, [studentId, dispatch]);

    useEffect(() => {
        if (studentId && feesList && feesList.length > 0) {
            const studentFees = feesList.filter(f => f.student?._id === studentId || f.student === studentId);
            const latestFee = studentFees.length > 0 ? studentFees[studentFees.length - 1] : null;
            if (latestFee) {
                // Previous dues should be the total outstanding balance (not 0) for Unpaid/Partial records.
                // Also ignore cases where backend totals are missing/incorrect by safely falling back to feeDetails.
                const outstandingFromTotals = (Number(latestFee.totalAmount) || 0) - (Number(latestFee.paidAmount) || 0);

                const calcOutstandingFromDetails = () => {
                    const details = latestFee.feeDetails || [];
                    return details.reduce((sum, d) => {
                        const amount = Number(d.amount) || 0;
                        const paid = Number(d.paidAmount) || 0;
                        return sum + Math.max(0, amount - paid);
                    }, 0);
                };

                const outstanding = outstandingFromTotals > 0 ? outstandingFromTotals : calcOutstandingFromDetails();
                setDuesAmount(outstanding > 0 ? outstanding.toString() : '');

                if (transportStations.length > 0) {
                    const feeDetails = latestFee.feeDetails || [];
                    for (let i = feeDetails.length - 1; i >= 0; i--) {
                        if (feeDetails[i].transportStation) {
                            const stationId = typeof feeDetails[i].transportStation === 'object' 
                                ? feeDetails[i].transportStation._id 
                                : feeDetails[i].transportStation;
                            
                            setSelectedStation(stationId);
                            const station = transportStations.find(s => s._id === stationId);
                            if (station) {
                                setCategoryAmounts(prev => {
                                    const activeCount = selectedMonths.length || 1;
                                    const newCategories = { ...prev, transportation: (station.price * activeCount).toString() };
                                    const total = Object.entries(newCategories).reduce((sum, [_, amt]) => {
                                        const val = typeof amt === 'number' ? amt : (parseFloat((amt || '').toString().replace(/,/g, '')) || 0);
                                        return sum + val;
                                    }, 0);
                                    setTotalMonthly(total);
                                    return newCategories;
                                });
                            }
                            break;
                        }
                    }
                }
            } else {
                setDuesAmount('');
            }
        } else if (studentId) {
            setDuesAmount('');
        }
    }, [feesList, studentId, transportStations]);

    const handleCategoryChange = (category, value) => {
        const newCategories = { ...categoryAmounts, [category]: value };
        setCategoryAmounts(newCategories);

        const total = Object.entries(newCategories).reduce((sum, [_, amt]) => sum + parseSafe(amt), 0);
        setTotalMonthly(total);
    };

    const buildFeePayload = () => {
        const totalFees = totalMonthly;
        const dues = parseFloat(duesAmount) || 0;
        const disc = parseFloat(discount) || 0;

        if (!studentId || selectedMonths.length === 0 || (totalFees + dues) === 0) {
            showToast("Please select at least one month and enter at least one fee amount.", "warning");
            return null;
        }

        const grossTotal = Math.max(0, totalFees + dues - disc);
        const advanceApplied = getAdvanceApplied(grossTotal);
        const totalToPay = Math.max(0, grossTotal - advanceApplied);
        const parsedPaidAmount = parseSafe(paidAmount);

        if (totalToPay > 0 && paymentStatus === 'Partial' && (parsedPaidAmount <= 0 || parsedPaidAmount >= totalToPay)) {
            showToast(parsedPaidAmount >= totalToPay
                ? "Paid amount for partial payment must be less than the total amount. Use 'Paid' status instead."
                : "Please enter a valid paid amount for partial payment.", "warning");
            setIsSubmitting(false);
            return null;
        }

        const categoriesLabelMap = Object.fromEntries(categories.map(c => [c.id, c.label]));
        const categoriesDesc = Object.entries(categoryAmounts)
            .filter(([_, amt]) => parseSafe(amt) > 0)
            .map(([cat, amt]) => `${categoriesLabelMap[cat] || cat}: Rs. ${parseSafe(amt)}`)
            .join(', ');

        const monthLabels = getNepaliMonthOptions()
            .filter(m => selectedMonths.includes(m.value))
            .map(m => m.label);

        const combinedMonthLabel = monthLabels.join(' - ');

        const newFeeDetail = {
            month: `${combinedMonthLabel} ${year}`,
            monthlyAmount: totalFees,
            duesAmount: dues,
            discount: disc,
            grossAmount: grossTotal,
            advanceUsed: advanceApplied,
            amount: totalToPay,
            description: `${categoriesDesc}${description ? ` - ${description}` : ''}${disc > 0 ? `, - Discount: Rs. ${disc}` : ''}${advanceApplied > 0 ? `, - Advance Used: Rs. ${advanceApplied}` : ''}`,
            status: totalToPay === 0 && advanceApplied > 0 ? 'Paid' : paymentStatus,
            paidAmount: totalToPay === 0 && advanceApplied > 0 ? 0 : (paymentStatus === 'Paid' ? totalToPay : (paymentStatus === 'Partial' ? parsedPaidAmount : 0)),
            paymentDate: (paymentStatus !== 'Unpaid' || (totalToPay === 0 && advanceApplied > 0)) ? payDate : null,
            transportStation: selectedStation || undefined
        };

        return {
            studentId,
            schoolId: currentUser._id,
            feeDetails: [newFeeDetail]
        };
    };

    const handleSaveDraft = async (e) => {
        e.preventDefault();

        const payload = buildFeePayload();
        if (!payload) return;

        setIsSubmitting(true);

        const state = location?.state;
        const mode = state?.mode;
        const draftId = state?.draftId;

        let response;
        if (mode === 'draft-edit' && draftId) {
            response = await dispatch(updateDraftFee(draftId, payload));
        } else {
            response = await dispatch(
                saveDraftFee({
                    ...payload,
                    billStatus: 'draft'
                })
            );
        }

        setIsSubmitting(false);

        // showToast is handled by backend response via existing redux status watcher,
        // but keep an immediate navigation and toast fallback.
        if (response?.success) {
            showToast('Draft bill saved successfully!', 'success');
            dispatch(underControl());
            navigate('/Admin/fees/drafts');

        } else {
            showToast(response?.message || 'Failed to save draft', 'error');
        }
    };

    const handleAddFee = async (e) => {
        e.preventDefault();

        const totalFees = totalMonthly;
        const dues = parseFloat(duesAmount) || 0;
        const disc = parseFloat(discount) || 0;

        if (!studentId || selectedMonths.length === 0 || (totalFees + dues) === 0) {
            showToast("Please select at least one month and enter at least one fee amount.", "warning");
            return;
        }

        const grossTotal = Math.max(0, totalFees + dues - disc);
        const advanceApplied = getAdvanceApplied(grossTotal);
        const totalToPay = Math.max(0, grossTotal - advanceApplied);
        const parsedPaidAmount = parseSafe(paidAmount);

        if (totalToPay > 0 && paymentStatus === 'Partial' && (parsedPaidAmount <= 0 || parsedPaidAmount >= totalToPay)) {
            showToast(parsedPaidAmount >= totalToPay
                ? "Paid amount for partial payment must be less than the total amount. Use 'Paid' status instead."
                : "Please enter a valid paid amount for partial payment.", "warning");
            setIsSubmitting(false);
            return;
        }

        setIsSubmitting(true);

        const state = location?.state;
        const mode = state?.mode;
        const draftId = state?.draftId;

        const categoriesLabelMap = Object.fromEntries(categories.map(c => [c.id, c.label]));
        const categoriesDesc = Object.entries(categoryAmounts)
            .filter(([_, amt]) => parseSafe(amt) > 0)
            .map(([cat, amt]) => `${categoriesLabelMap[cat] || cat}: Rs. ${parseSafe(amt)}`)
            .join(', ');

        const monthLabels = getNepaliMonthOptions()
            .filter(m => selectedMonths.includes(m.value))
            .map(m => m.label);

        const combinedMonthLabel = monthLabels.join(' - ');

        const newFeeDetail = {
            month: `${combinedMonthLabel} ${year}`,
            monthlyAmount: totalFees,
            duesAmount: dues,
            discount: disc,
            grossAmount: grossTotal,
            advanceUsed: advanceApplied,
            amount: totalToPay,
            description: `${categoriesDesc}${description ? ` - ${description}` : ''}${disc > 0 ? `, - Discount: Rs. ${disc}` : ''}${advanceApplied > 0 ? `, - Advance Used: Rs. ${advanceApplied}` : ''}`,
            status: totalToPay === 0 && advanceApplied > 0 ? 'Paid' : paymentStatus,
            paidAmount: totalToPay === 0 && advanceApplied > 0 ? 0 : (paymentStatus === 'Paid' ? totalToPay : (paymentStatus === 'Partial' ? parsedPaidAmount : 0)),
            paymentDate: (paymentStatus !== 'Unpaid' || (totalToPay === 0 && advanceApplied > 0)) ? payDate : null,
            transportStation: selectedStation || undefined
        };

        if (mode === 'draft-edit' && draftId) {
            // Update draft first, then generate bill
            const payload = {
                studentId,
                schoolId: currentUser._id,
                feeDetails: [newFeeDetail]
            };
            const updateRes = await dispatch(updateDraftFee(draftId, payload));
            if (updateRes?.success) {
                const genRes = await dispatch(generateBillFromDraft(draftId));
                setIsSubmitting(false);
                if (genRes?.success) {
                    const receiptData = extractLatestReceiptData(genRes.data, sclassesList);
                    if (receiptData) setSavedReceiptData(receiptData);
                    if (genRes.data?.student?.advanceBalance !== undefined) {
                        setAdvanceBalanceOverride(Number(genRes.data.student.advanceBalance) || 0);
                    }
                    showToast("Fee record created successfully!", "success");
                    setIsSuccess(true);
                    if (studentId) dispatch(getStudentFee(studentId));
                    dispatch(getAllStudents(currentUser._id));
                } else {
                    showToast(genRes?.message || "Failed to generate bill from draft", "error");
                }
            } else {
                setIsSubmitting(false);
                showToast(updateRes?.message || "Failed to update draft", "error");
            }
        } else {
            const data = {
                studentId: studentId,
                schoolId: currentUser._id,
                billStatus: 'generated',
                feeDetails: [newFeeDetail]
            };

            const addRes = await dispatch(addFee(data));
            setIsSubmitting(false);
            if (addRes?.success) {
                const receiptData = extractLatestReceiptData(addRes.data, sclassesList);
                if (receiptData) setSavedReceiptData(receiptData);
                if (addRes.data?.student?.advanceBalance !== undefined) {
                    setAdvanceBalanceOverride(Number(addRes.data.student.advanceBalance) || 0);
                }
                setIsSuccess(true);
                showToast("Fee record created successfully!", "success");
                if (studentId) dispatch(getStudentFee(studentId));
                dispatch(getAllStudents(currentUser._id));
            }
        }
    };

    const filteredStudents = selectedClass
        ? studentsList?.filter(s => s.sclassName?._id === selectedClass)
        : studentsList;

    // Past fee history when arriving from /Admin/fees
    useEffect(() => {
        const state = location?.state;
        if (state?.from !== 'feesList') return;

        const targetStudentId = state?.studentId;
        if (!targetStudentId) return;

        const classId = state?.classId || '';
        const className = state?.className || '';
        const clickedMonth = state?.month; // optional (may exist)
        const clickedYear = state?.year;   // optional (may exist)

        setIsPrefillingFromFeesList(true);

        setStudentId(targetStudentId);
        if (classId) setSelectedClass(classId);

        // Prefill months:
        // If caller provided a single clicked month, use it.
        // Otherwise fall back to any month/year from fee history after feesList loads.
        if (clickedMonth && clickedYear) {
            setYear(clickedYear.toString());
            // month value must match getNepaliMonthOptions().value
            const monthObj = getNepaliMonthOptions().find(m => m.label === clickedMonth || m.value === clickedMonth);
            if (monthObj) {
                setSelectedMonths([monthObj.value]);
                setMonth(monthObj.value);
            }
        }

        // Ensure feesList is available (sometimes Redux may not yet have loaded fees)
        if ((!feesList || !Array.isArray(feesList) || feesList.length === 0) && currentUser?._id) {
            dispatch(getAllFees(currentUser._id));
            return;
        }

        const list = (feesList || [])
            .filter((f) => f?.student?._id === targetStudentId || f?.student === targetStudentId)
            .flatMap((f) => (Array.isArray(f.feeDetails) ? f.feeDetails.map((d) => ({
                ...d,
                feeRecordId: f._id,
                className: f?.student?.sclassName?.sclassName || className || 'N/A',
                studentId: f?.student?._id,
                studentName: f?.student?.name || state?.studentName || 'Unknown',
            })) : []));

        setFeeHistory(list.slice().reverse());

        // If we have not selected months yet, infer months from the latest matching fee detail.
        if (!selectedMonths?.length && list.length) {
            const latest = list[list.length - 1];
            const detailMonthStr = (latest?.month || '').trim();
            const lastSpace = detailMonthStr.lastIndexOf(' ');
            const yearPart = lastSpace >= 0 ? detailMonthStr.slice(lastSpace + 1) : year;
            const monthPart = lastSpace >= 0 ? detailMonthStr.slice(0, lastSpace) : detailMonthStr;
            const monthLabels = monthPart.split('-').map(s => s.trim()).filter(Boolean);
            const months = getNepaliMonthOptions().filter(m => monthLabels.includes(m.label)).map(m => m.value);

            if (months.length) {
                setYear(yearPart || '2083');
                setSelectedMonths(months);
                setMonth(months[months.length - 1]);
            }

            // Prefill category amounts from latest detail description if possible
            const desc = latest?.description || '';
            if (desc) {
                const { categoryAmounts: parsedCategoryAmounts, additionalNotes } = parseStoredFeeDescription(
                    desc,
                    categories
                );

                setCategoryAmounts(prev => ({ ...prev, ...parsedCategoryAmounts }));
                setTotalMonthly(Number(latest?.monthlyAmount) || 0);
                setDuesAmount(latest?.duesAmount ? String(latest?.duesAmount) : '');
                setDescription(additionalNotes);
                setPaymentStatus(latest?.status || 'Unpaid');
                setPaidAmount(latest?.paidAmount !== undefined && latest?.paidAmount !== null ? String(latest?.paidAmount) : '');
            }
        }
    }, [location?.state, feesList, currentUser?._id, dispatch]);

    const getMonthIndicatorStyles = (billStatus) => {
        if (billStatus === 'draft') {
            return {
                menuBg: '#fff7ed',
                menuBorder: '#fdba74',
                menuText: '#9a3412',
                chipBg: '#ffedd5',
                chipText: '#9a3412',
                dot: '#f97316'
            };
        }

        return {
            menuBg: '#eff6ff',
            menuBorder: '#93c5fd',
            menuText: '#1d4ed8',
            chipBg: '#dbeafe',
            chipText: '#1d4ed8',
            dot: '#2563eb'
        };
    };

    // Apply student fee overrides (if any) on top of class FeeSetup values.
    useEffect(() => {
        if (!studentId) return;

        const student = studentsList?.find(s => s._id === studentId);
        if (!student) return;

        const tuition = student.tuitionFee;
        const admission = student.admissionFee;
        const annual = student.annualFee;
        const computer = student.computerFee;
        const extras = student.otherFees;

        const hasAnyOverride =
            (tuition !== undefined && tuition !== null && tuition !== 0) ||
            (admission !== undefined && admission !== null && admission !== 0) ||
            (annual !== undefined && annual !== null && annual !== 0) ||
            (computer !== undefined && computer !== null && computer !== 0) ||
            (extras !== undefined && extras !== null && extras !== 0);

        if (!hasAnyOverride) return;

        const activeCount = selectedMonths.length || 1;

        setCategoryAmounts(prev => {
            const next = { ...prev };
            if (tuition !== undefined && tuition !== null && tuition !== 0) next.tuition = (Number(tuition) * activeCount).toString();
            if (admission !== undefined && admission !== null && admission !== 0) next.admission = (Number(admission) * 1).toString();
            if (annual !== undefined && annual !== null && annual !== 0) next.annual = (Number(annual) * 1).toString(); // one-time, not per month
            if (computer !== undefined && computer !== null && computer !== 0) next.computer = (Number(computer) * activeCount).toString();
            if (extras !== undefined && extras !== null && extras !== 0) next.extras = (Number(extras) * activeCount).toString();
            return next;
        });

        // Recompute total reliably after override
        setTimeout(() => {
            setCategoryAmounts(prev => {
                const total = Object.entries(prev).reduce((sum, [_, amt]) => sum + parseSafe(amt), 0);
                setTotalMonthly(total);
                return prev;
            });
        }, 0);

    }, [studentId, studentsList, selectedMonths]);


    return (
        <PageContainer>
            <HeaderWrapper elevation={0}>
                <Container maxWidth="xl">
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 3 }}>
                        <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: 'white' }}>Generate Fee Record</Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>Select class and student to create new billing entry</Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                                                            <Tooltip title="Print Preview">
                                                                <span>
                                                                    <IconButton
                                                                        onClick={() => setOpenPreview(true)}
                                                                        disabled={!studentId || selectedMonths.length === 0 || totalMonthly === 0}
                                                                        sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}
                                                                    >
                                                                        <VisibilityIcon />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>

                        </Stack>
                    </Stack>
                </Container>
            </HeaderWrapper>

            <Container maxWidth="xl" sx={{ mt: 2, height: 'calc(100vh - 120px)', overflow: 'hidden', pb: 2 }}>
                <Grid container spacing={3} sx={{ height: '100%' }}>
                    {/* Left Sidebar: Class Selection */}
                    <Grid item xs={12} md={3} sx={{ height: '100%' }}>
                        <Box sx={{ height: '100%' }}>
                            <SectionCard elevation={2}>
                            <SectionHeader>
                                <ClassIcon sx={{ color: '#667eea' }} />
                                <Typography variant="subtitle1" fontWeight="700">Select Class</Typography>
                            </SectionHeader>
                            <Box sx={{ p: 2, flex: 1, overflowY: 'auto', '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#e2e8f0', borderRadius: '10px' } }}>
                                <Stack spacing={1}>
                                    <ClassItem
                                        $active={selectedClass === ""}
                                        onClick={() => {
                                            setSelectedClass("");
                                            setStudentId('');
                                        }}
                                        startIcon={<ClassIcon />}
                                    >
                                        All Classes
                                    </ClassItem>
                                    {sclassesList?.map(c => (
                                        <ClassItem
                                            key={c._id}
                                            $active={selectedClass === c._id}
                                            onClick={() => {
                                                setSelectedClass(c._id);
                                                setStudentId('');
                                            }}
                                            startIcon={<ClassIcon />}
                                        >
                                            {c.sclassName}
                                        </ClassItem>
                                    ))}
                                </Stack>
                            </Box>
                        </SectionCard>
                        </Box>
                    </Grid>

                    {/* Right Side: Form Content */}
                    <Grid item xs={12} md={9} sx={{ height: '100%', overflowY: 'auto', pr: 1, '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#e2e8f0', borderRadius: '10px' } }}>
                        <StyledForm onSubmit={handleAddFee}>
                            <Grid container spacing={3}>
                                {/* Student and Month Selection */}
                                <Grid item xs={12} >
                                    <SectionCard elevation={2}>
                                        <SectionHeader color="#667eea">
                                            <PersonIcon />
                                            <Typography variant="subtitle1" fontWeight="700" color="white">Target Student & Period</Typography>
                                        </SectionHeader>
                                        <Box sx={{ p: 3 }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Autocomplete
                                                        options={filteredStudents || []}
                                                        value={filteredStudents?.find(s => s._id === studentId) || null}
                                                        onChange={(event, newValue) => {
                                                            setStudentId(newValue?._id || '');
                                                        }}
                                                        getOptionLabel={(option) => option?.name ? option.name : ''}
                                                        filterOptions={(options, { inputValue }) => {
                                                            const q = (inputValue || '').toLowerCase().trim();
                                                            if (!q) return options;
                                                            return options.filter((opt) => (opt?.name || '').toLowerCase().includes(q));
                                                        }}
                                                        isOptionEqualToValue={(option, value) => option?._id === value?._id}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Select Student"
                                                                required
                                                                fullWidth
                                                                InputLabelProps={{ shrink: true }}
                                                                placeholder={selectedClass ? 'Search student by name...' : 'Search student by name (all classes)...'}
                                                            />
                                                        )}
                                                        renderOption={(props, option) => (
                                                            <li {...props} key={option._id}>
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: '#667eea' }}>
                                                                        {option?.name?.[0]}
                                                                    </Avatar>
                                                                    <Box>
                                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                            {option?.name} (Roll: {option?.rollNum})
                                                                        </Typography>
                                                                    </Box>
                                                                </Stack>
                                                            </li>
                                                        )}
                                                        disableClearable
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Stack direction="row" spacing={1}>
                                                        <FormControl fullWidth required>
                                                            <InputLabel>Year</InputLabel>
                                                            <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
                                                                {getNepaliYearOptions().map(y => <MenuItem key={y} value={y.toString()}>{y}</MenuItem>)}
                                                            </Select>
                                                        </FormControl>

                                                        <FormControl fullWidth required>
                                                            <InputLabel>Months</InputLabel>
                                                            <Select
                                                                multiple
                                                                value={selectedMonths}
                                                                label="Months"
                                                                renderValue={(selected) => {
                                                                    const selectedValues = Array.isArray(selected) ? selected : [];
                                                                    if (selectedValues.length === 0) return '';

                                                                    return (
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                            {selectedValues.map((value) => {
                                                                                const option = getNepaliMonthOptions().find(m => m.value === value);
                                                                                const lockInfo = lockedMonthsByValue.get(value);
                                                                                const indicatorStyles = lockInfo ? getMonthIndicatorStyles(lockInfo.billStatus) : null;
                                                                                return (
                                                                                    <Chip
                                                                                        key={value}
                                                                                        label={lockInfo
                                                                                            ? `${option?.label || value} • ${lockInfo.billStatus === 'draft' ? 'Draft exists' : 'Billed'}`
                                                                                            : (option?.label || value)}
                                                                                        size="small"
                                                                                        sx={{
                                                                                            height: 24,
                                                                                            bgcolor: indicatorStyles?.chipBg || '#eef2ff',
                                                                                            color: indicatorStyles?.chipText || '#3730a3',
                                                                                            fontWeight: 700
                                                                                        }}
                                                                                    />
                                                                                );
                                                                            })}
                                                                        </Box>
                                                                    );
                                                                }}
                                                                onChange={(e) => {
                                                                    const vals = e.target.value;
                                                                    const arr = Array.isArray(vals) ? vals : [vals];
                                                                    setSelectedMonths(arr);
                                                                    setMonth(arr.length ? arr[arr.length - 1] : '');
                                                                }}
                                                            >
                                                                {getNepaliMonthOptions().map(m => {
                                                                    const lockInfo = lockedMonthsByValue.get(m.value);
                                                                    const indicatorStyles = lockInfo ? getMonthIndicatorStyles(lockInfo.billStatus) : null;
                                                                    return (
                                                                    <MenuItem
                                                                        key={m.value}
                                                                        value={m.value}
                                                                        sx={{
                                                                            ...(indicatorStyles ? {
                                                                                bgcolor: indicatorStyles.menuBg,
                                                                                borderLeft: `3px solid ${indicatorStyles.menuBorder}`,
                                                                                '&:hover': {
                                                                                    bgcolor: indicatorStyles.menuBg
                                                                                }
                                                                            } : {})
                                                                        }}
                                                                    >
                                                                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: '100%' }}>
                                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                                {lockInfo && (
                                                                                    <Box
                                                                                        sx={{
                                                                                            width: 8,
                                                                                            height: 8,
                                                                                            borderRadius: '50%',
                                                                                            bgcolor: indicatorStyles.dot,
                                                                                            flexShrink: 0
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{
                                                                                        fontWeight: lockInfo ? 800 : 500,
                                                                                        color: indicatorStyles?.menuText || 'inherit'
                                                                                    }}
                                                                                >
                                                                                {m.label}
                                                                                </Typography>
                                                                            </Stack>
                                                                            {lockInfo && (
                                                                                <Chip
                                                                                    label={lockInfo.billStatus === 'draft' ? 'Draft exists' : 'Billed'}
                                                                                    size="small"
                                                                                    sx={{
                                                                                        height: 22,
                                                                                        bgcolor: indicatorStyles.chipBg,
                                                                                        color: indicatorStyles.chipText,
                                                                                        fontWeight: 800,
                                                                                        border: `1px solid ${indicatorStyles.menuBorder}`
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </Stack>
                                                                    </MenuItem>
                                                                );})}
                                                            </Select>
                                                        </FormControl>
                                                    </Stack>
                                                </Grid>
                                                {transportStations.length > 0 && (
                                                    <Grid item xs={12}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>Transport Station (Optional)</InputLabel>
                                                            <Select 
                                                                value={selectedStation} 
                                                                label="Transport Station (Optional)" 
                                                                onChange={handleStationChange}
                                                            >
                                                                <MenuItem value=""><em>None</em></MenuItem>
                                                                {transportStations.map(station => (
                                                                    <MenuItem key={station._id} value={station._id}>
                                                                        {station.station} (Rs. {station.price})
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Box>
                                    </SectionCard>
                                </Grid>

                                {/* Past Fee History (removed from AddFee) */}
                                {false && feeHistory && feeHistory.length > 0 && (
                                    <Grid item xs={12}>

                                        <SectionCard elevation={2}>
                                            <SectionHeader color="#667eea">
                                                <HistoryIcon />
                                                <Typography variant="subtitle1" fontWeight="700" color="white">
                                                    Past Fee History {feeHistoryClassName ? `- ${feeHistoryClassName}` : ''}
                                                </Typography>
                                            </SectionHeader>
                                            <Box sx={{ p: 2 }}>
                                                <Grid container spacing={1}>
                                                    {feeHistory.map((h, idx) => (
                                                        <Grid item xs={12} key={`${h.feeRecordId || 'rec'}-${idx}`}>
                                                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '12px', bgcolor: '#fff' }}>
                                                                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                                                    <Box>
                                                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                                                            {h.month}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="textSecondary">
                                                                            Status: {h.status} • Paid: Rs.{Number(h.paidAmount || 0).toLocaleString()}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box textAlign="right">
                                                                        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                                                                            Amount: Rs.{Number(h.amount || 0).toLocaleString()}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="textSecondary">
                                                                            Payment Date: {h.paymentDate ? formatDatePart(h.paymentDate) : '-'}
                                                                        </Typography>
                                                                    </Box>
                                                                </Stack>
                                                            </Paper>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </Box>
                                        </SectionCard>
                                    </Grid>
                                )}

                                {/* Fee Categories */}
                                <Grid item xs={12}>
                                    <SectionCard elevation={2}>
                                        <SectionHeader color="#81c784">
                                            <WalletIcon />
                                            <Typography variant="subtitle1" fontWeight="700" color="white">Monthly Fee Breakdown (Rs.)</Typography>
                                        </SectionHeader>
                                        <Box sx={{ p: 3 }}>
                                            <Grid container spacing={2}>
                                                {categories.map((cat) => (
                                                    <Grid item xs={12} sm={4} key={cat.id}>
                                                        <TextField
                                                            fullWidth
                                                            label={cat.label}
                                                            type="number"
                                                            size="small"
                                                            value={categoryAmounts[cat.id] || ''}
                                                            onChange={(e) => handleCategoryChange(cat.id, e.target.value)}
                                                            InputProps={{
                                                                startAdornment: <InputAdornment position="start" sx={{ color: cat.color }}>{cat.icon}</InputAdornment>,
                                                            }}
                                                        />
                                                    </Grid>
                                                ))}
                                                <Grid item xs={12}>
                                                    <Divider sx={{ my: 1 }} />
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                                                        <Typography variant="body2" color="textSecondary" fontWeight="600">Total Monthly:</Typography>
                                                        <Typography variant="h5" color="primary" fontWeight="800">Rs.{totalMonthly.toLocaleString()}</Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </SectionCard>
                                </Grid>

                                {/* Additional and Finalize */}
                                <Grid item xs={12}>
                                    <SectionCard elevation={2}>
                                        <SectionHeader color="#ffb74d">
                                            <EventIcon />
                                            <Typography variant="subtitle1" fontWeight="700" color="white">Due Details & Finalize</Typography>
                                        </SectionHeader>
                                        <Box sx={{ p: 3 }}>
                                            <Grid container spacing={3}>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Previous Dues"
                                                        type="number"
                                                        value={duesAmount}
                                                        onChange={(e) => setDuesAmount(e.target.value)}
                                                        helperText="Outstanding balance from previous months"
                                                        InputProps={{ startAdornment: <InputAdornment position="start">Rs.</InputAdornment> }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Discount"
                                                        type="number"
                                                        value={discount}
                                                        onChange={(e) => setDiscount(e.target.value)}
                                                        helperText="Discount amount to deduct from total"
                                                        InputProps={{ startAdornment: <InputAdornment position="start">Rs.</InputAdornment> }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Box sx={{ p: 2, borderRadius: '12px', border: '1px solid #dbeafe', bgcolor: '#eff6ff' }}>
                                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                                            <Box sx={{ flexGrow: 1 }}>
                                                                <Typography variant="caption" sx={{ color: '#1d4ed8', fontWeight: 800 }}>
                                                                    ADVANCE BALANCE
                                                                </Typography>
                                                                <Typography variant="h6" sx={{ color: '#1e3a8a', fontWeight: 900 }}>
                                                                    Rs.{currentAdvanceBalance.toLocaleString()}
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant="body2" sx={{ color: '#1d4ed8', fontWeight: 700 }}>
                                                                Available advance will be deducted automatically when the bill is generated.
                                                            </Typography>
                                                        </Stack>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <CustomNepaliDatePicker
                                                        label="Pay Date (Issue Date)"
                                                        value={payDate}
                                                        onChange={(date) => setPayDate(date)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        label="Additional Notes"
                                                        multiline
                                                        rows={2}
                                                        value={description}
                                                        onChange={(e) => setDescription(e.target.value)}
                                                        placeholder="e.g. Scholarship applied, sibling discount, etc."
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>Payment Status</InputLabel>
                                                        <Select
                                                            value={paymentStatus}
                                                            label="Payment Status"
                                                            onChange={(e) => setPaymentStatus(e.target.value)}
                                                        >
                                                            <MenuItem value="Unpaid">Unpaid</MenuItem>
                                                            <MenuItem value="Paid">Paid</MenuItem>
                                                            <MenuItem value="Partial">Partial</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                {paymentStatus === 'Partial' && (
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField
                                                            fullWidth
                                                            label="Paid Amount"
                                                            type="number"
                                                            value={paidAmount}
                                                            onChange={(e) => setPaidAmount(e.target.value)}
                                                            InputProps={{ startAdornment: <InputAdornment position="start">Rs.</InputAdornment> }}
                                                            required
                                                        />
                                                    </Grid>
                                                )}
                                                <Grid item xs={12}>
                                                        <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', mb: 2 }}>
                                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                <Box>
                                                                    <Typography variant="caption" color="textSecondary">GRAND TOTAL TO PAY</Typography>
                                                                    <Typography variant="h4" sx={{ fontWeight: 900, color: '#1a1a2e' }}>
                                                                        Rs.{Math.max(
                                                                            0,
                                                                            Math.max(0, totalMonthly + (parseFloat(duesAmount) || 0) - (parseFloat(discount) || 0)) -
                                                                            getAdvanceApplied(Math.max(0, totalMonthly + (parseFloat(duesAmount) || 0) - (parseFloat(discount) || 0)))
                                                                        ).toLocaleString()}
                                                                    </Typography>
                                                                    {getAdvanceApplied(Math.max(0, totalMonthly + (parseFloat(duesAmount) || 0) - (parseFloat(discount) || 0))) > 0 && (
                                                                        <Typography variant="caption" sx={{ color: '#2563eb', fontWeight: 800 }}>
                                                                            Advance used: Rs.{getAdvanceApplied(Math.max(0, totalMonthly + (parseFloat(duesAmount) || 0) - (parseFloat(discount) || 0))).toLocaleString()}
                                                                        </Typography>
                                                                    )}
                                                            </Box>
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                            <Button
                                                                variant="outlined"
                                                                disabled={isSubmitting}
                                                                onClick={handleSaveDraft}
                                                                sx={{
                                                                    borderRadius: '14px',
                                                                    textTransform: 'none',
                                                                    fontWeight: 800,
                                                                    borderColor: '#667eea',
                                                                    color: '#667eea',
                                                                    px: 3
                                                                }}
                                                            >
                                                                Save Draft
                                                            </Button>
                                                            <SubmitButton
                                                                type="submit"
                                                                variant="contained"
                                                                disabled={loading || isSubmitting}
                                                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                                            >

                                                                    {loading ? 'Processing...' : 'Create Fee Record'}
                                                                </SubmitButton>
                                                            </Stack>
                                                        </Stack>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </SectionCard>
                                </Grid>
                            </Grid>
                        </StyledForm>
                    </Grid>
                </Grid>
            </Container>

            {/* Print Preview Dialog */}
            <FeeReceipt
                open={openPreview}
                onClose={() => {
                    setOpenPreview(false);
                    setIsPrintingSaved(false);
                    setSavedReceiptData(null);
                    if (status === "added") navigate('/Admin/fees');
                }}
                feeData={previewReceiptData}
            />

            {/* Success Dialog */}
            <Dialog open={isSuccess} onClose={() => { }} fullWidth maxWidth="sm">
                <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                    <Avatar sx={{ bgcolor: '#4caf50', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                        <CheckCircleIcon sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h5" fontWeight="800" gutterBottom>Success!</Typography>
                    <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                        Fee record has been created successfully for <strong>{studentsList?.find(s => s._id === studentId)?.name}</strong>.
                    </Typography>
                    <Stack direction="column" spacing={2}>
                        {/* <Button
                            variant="contained"
                            fullWidth
                            startIcon={<PrintIcon />}
                            onClick={() => {
                                setIsPrintingSaved(true);
                                setOpenPreview(true);
                                setIsSuccess(false);
                            }}
                            sx={{ borderRadius: '12px', py: 1.5, fontWeight: 700, textTransform: 'none' }}
                        >
                            Print Receipt Now
                        </Button> */}
                        <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                            Go to fee list for printing </Typography>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => {
                                dispatch(underControl());
                                navigate('/Admin/fees');
                            }}
                            sx={{ borderRadius: '12px', py: 1.5, fontWeight: 700, textTransform: 'none' }}
                        >
                            Go to Fee List
                        </Button>
                        <Button
                            variant="text"
                            fullWidth
                            onClick={() => {
                                setIsSuccess(false);
                                // Reset form for next entry
                                setStudentId('');
                                setCategoryAmounts(Object.fromEntries(categories.map(c => [c.id, ''])));
                                setTotalMonthly(0);
                                setDuesAmount('');
                                setSelectedStation('');
                                prevMonthCountRef.current = selectedMonths.length || 1;
                            }}
                            sx={{ fontWeight: 600, textTransform: 'none' }}
                        >
                            Create Another Record
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
};

export default AddFee;

const PageContainer = styled.div`background: #f8fafc; min-height: 100vh; animation: ${fadeIn} 0.5s ease-out;`;
const HeaderWrapper = styled(Paper)`background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%); border-radius: 0 0 40px 40px !important; padding-bottom: 20px;`;
const StyledForm = styled.form``;
const SectionCard = styled(Paper)`border-radius: 20px !important; overflow: hidden; border: 1px solid #e2e8f0; display: flex; flex-direction: column; height: 100%;`;
const SectionHeader = styled(Box)`padding: 12px 24px; background: ${props => props.color || '#f8fafc'}; color: ${props => props.color ? 'white' : '#1e293b'}; display: flex; align-items: center; gap: 12px;`;
const SubmitButton = styled(Button)`
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important; color: white !important;
    padding: 12px 30px !important; border-radius: 14px !important; font-weight: 700 !important;
    text-transform: none !important; box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4) !important;
    &:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5) !important; }
`;

const ClassItem = styled(Button)`
    width: 100%;
    justify-content: flex-start !important;
    text-align: left !important;
    padding: 12px 16px !important;
    margin-bottom: 8px !important;
    border-radius: 12px !important;
    text-transform: none !important;
    font-weight: 600 !important;
    color: ${props => props.$active ? '#fff' : '#64748b'} !important;
    background: ${props => props.$active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent'} !important;
    border: 1px solid ${props => props.$active ? 'transparent' : '#e2e8f0'} !important;
    box-shadow: ${props => props.$active ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none'} !important;
    transition: all 0.2s ease !important;

    &:hover {
        background: ${props => props.$active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f1f5f9'} !important;
        transform: translateX(4px);
    }

    svg {
        margin-right: 12px;
        color: ${props => props.$active ? '#fff' : '#667eea'};
    }
`;
